export interface NavItem {
    id: string
    label: string
    ja: string
}

export const NAV_ITEMS: Array<NavItem> = [
    { id: 'top', label: "TOP", ja: "トップ" },
    { id: 'profile', label: "PROFILE", ja: "プロフィール" },
    { id: 'world', label: "WORLD", ja: "世界観" },
    { id: 'story', label: "STORY", ja: "ストーリー" },
    { id: 'stream', label: "STREAM", ja: "配信" },
    { id: 'music', label: "MUSIC", ja: "楽曲" },
    { id: 'goods', label: "GOODS", ja: "グッズ" },
    { id: 'works', label: "WORKS", ja: "実績" },
    { id: 'contact', label: "CONTACT", ja: "お問い合わせ" }
]

// Writing rules for every text on the site (from the official setting sheet):
// 天満ニア / AMAMA NIA, MAHO / マホ, マホニア観測局, always 「電子世界」 (never 電脳世界),
// Nia and MAHO are 「一人と一機」, 局長 = 天満ニア, 副長 = MAHO, fans = 局員 (観測局員).
// Leave what the sheet does not settle (the ANIA Project's past) as open fragments.

export const OPENING_COPY = {
    greeting: "Hello World!",
    status: "マホニア観測局 観測端末に接続しています"
}

export const HERO_COPY = {
    title: "AMAMA NIA",
    titleLines: ["AMAMA", "NIA"],
    catch: "ゲームと音楽の物語を旅する観測者。",
    // Columns of the vertical catchcopy, read right to left.
    catchLines: ["ゲームと音楽の物語を", "旅する観測者。"],
    catchEn: "OBSERVER OF THE ENDLESS SUMMER",
    sub: "相棒のMAHOと、電子世界をめぐる旅の途中。",
    log: "MAHONIA OBSERVATORY",
    coords: "ACCESS ✦ ELECTRONIC WORLD",
    day: "SEASON — ENDLESS SUMMER",
    scroll: "観測記録を閲覧する"
}

export interface ProfileRow {
    label: string
    ja: string
    value: string
    wide?: boolean
}

export const PROFILE_COPY = {
    index: "01",
    kicker: "PROFILE 1",
    title: "NIA",
    ja: "天満ニア / AMAMA NIA",
    lead: "たった一人と一機の観測局、マホニア観測局の局長。",
    // Lines of one paragraph (see CopyLines).
    body: [
        "さまざまな作品に宿る物語を観測し、",
        "記録として残す旅を続けている。"
    ],
    data: [
        { label: "OFFICE", ja: "所属", value: "マホニア観測局" },
        { label: "POSITION", ja: "役職", value: "局長 / 観測者" },
        { label: "BIRTHDAY", ja: "誕生日", value: "10月5日" },
        { label: "SPECIES", ja: "種族", value: "狐宿りの人間" },
        { label: "ACTIVITY", ja: "活動", value: "ゲーム実況・歌・雑談・ASMR・交流企画", wide: true }
    ] as Array<ProfileRow>
}

// Nia's standing art. Add an entry here (image in public/images, on a pure white background)
// to give the profile another expression, form or outfit; the switcher picks it up on its own.
export interface CharacterVariant {
    id: string
    label: string
    src: string
    width: number
    height: number
    alt: string
}

export const NIA_VARIANTS: Array<CharacterVariant> = [
    {
        id: 'default',
        label: "通常",
        src: '/images/profile-character.png',
        width: 1792,
        height: 2400,
        alt: "カメラを構えて振り返る天満ニア"
    },
    {
        id: 'smile',
        label: "笑顔",
        src: '/images/profile-character-smile.png',
        width: 1792,
        height: 2400,
        alt: "目を細めて笑う天満ニア"
    },
    {
        id: 'full',
        label: "全身",
        src: '/images/kv-character.png',
        width: 1696,
        height: 2528,
        alt: "カメラを手に立つ天満ニアの全身"
    }
]

export const MAHO_COPY = {
    index: "02",
    kicker: "PROFILE 2",
    title: "MAHO",
    ja: "観測補助ユニット / マホ",
    lead: "ニアとともに旅をする、小型の浮遊型観測補助ロボット。",
    body: [
        "観測・調査・記録を支える、マホニア観測局の副長。",
        "好奇心旺盛で、顔のディスプレイに感情を映す。",
        "ニアと「一人と一機」で旅を続ける、大切な相棒。"
    ],
    data: [
        { label: "ID", ja: "識別番号", value: "ANIA-X08-1040028", wide: true },
        { label: "CALL SIGN", ja: "コールサイン", value: "ATLAS" },
        { label: "POSITION", ja: "役職", value: "副長" },
        { label: "CLASS", ja: "分類", value: "観測補助ユニット / 試作機", wide: true }
    ] as Array<ProfileRow>,
    image: {
        src: '/images/maho.png',
        width: 2048,
        height: 2048,
        alt: "白とクリーム色の小型浮遊ロボット、MAHO"
    }
}

// WORLD is about the place: an old facility once used for a large plan (the ANIA Project, hinted
// only on the map), whose leftover equipment Nia and MAHO still use. One corner of it is the
// observatory. Never explain its past.
export const WORLD_COPY = {
    index: "03",
    kicker: "SITE",
    title: "WORLD",
    ja: "電子世界 / 古い施設跡",
    lead: "電子世界の片隅に残る、古い施設跡。",
    // Paragraphs, each a list of lines (see CopyLines).
    paragraphs: [
        ["かつては、ある大きな計画のために使われていたらしい。"],
        ["残された設備を使い、", "一人と一機は、いまも観測を続けている。"],
        ["その一角が、現在のマホニア観測局。"]
    ],
    note: "施設に残る記録の多くは、まだ読み解かれていない。",
    map: {
        title: "SITE MAP — ANIA FACILITY",
        status: "RECORD INCOMPLETE",
        label: "マホニア観測局のある施設跡の地図"
    },
    dormitory: {
        code: "POINT ✦ DORM",
        name: "局員寮",
        text: "マホニア観測局の局員たちが集う寮。",
        cta: "入寮案内を見る",
        url: 'https://amamania.fanbox.cc/'
    }
}

export const STORY_COPY = {
    index: "04",
    kicker: "RECORD",
    title: "STORY",
    ja: "観測記録 No.0001",
    quote: [
        "「忘れられない物語を求めて、",
        "いくつもの世界をめぐる観測者。」"
    ],
    paragraphs: [
        ["相棒の観測補助ユニットMAHOとともに、", "ゲームや音楽に宿る物語を観測し、", "その記録を残しながら旅を続けている。"],
        ["旅の途中で出会った仲間たちと、", "今度は自分たちの歌声で、", "誰かの心に残る新しい物語を紡いでいく。"]
    ],
    // A fragment of an old research file. Only what the setting sheet states; the rest stays unread.
    archive: {
        label: "ARCHIVE ✦ ANIA PROJECT",
        fragment: "FRAGMENT 01 / ??",
        name: "Adaptive Navigation and Intelligence Archive",
        lines: [
            "かつて存在した研究・観測計画。",
            "計画の終了後も、ニアとMAHOは観測を続けている。"
        ],
        missing: "終了の経緯",
        missingValue: "記録が見つかりません"
    }
}

// STORY record cards, drawn as small geometric compositions (see decor/pattern-art).
export interface RecordPhoto {
    art: 'tower' | 'observatory' | 'sky' | 'night'
    code: string
    caption: string
    time: string
    label: string
}

export const RECORD_PHOTOS: Array<RecordPhoto> = [
    {
        art: 'observatory',
        code: "REC.0008",
        caption: "ANIA 計画書の断片",
        time: "DATA LOST",
        label: "古い研究資料を表す幾何学模様の記録カード"
    },
    {
        art: 'tower',
        code: "REC.0412",
        caption: "観測ログ",
        time: "08.12 14:22",
        label: "観測ログを表す幾何学模様の記録カード"
    },
    {
        art: 'sky',
        code: "REC.1040",
        caption: "通信記録",
        time: "10.05 00:00",
        label: "通信記録を表す幾何学模様の記録カード"
    },
    {
        art: 'night',
        code: "REC.0028",
        caption: "未整理の記録",
        time: "--.-- --:--",
        label: "まだ整理されていない記録を表す幾何学模様の記録カード"
    }
]

export const STREAM_COPY = {
    index: "05",
    title: "STREAM",
    kicker: "LIVE STREAMING",
    ja: "配信情報",
    lead: ["ゲーム実況、歌、雑談、ASMR、交流企画。", "電子世界からの通信を、YouTube で届けています。"],
    empty: ["いまお知らせできる配信はありません。", "次の通信をお待ちください。"],
    watch: "YouTube で見る",
    channel: "YouTube チャンネル"
}

export const MUSIC_COPY = {
    index: "06",
    title: "MUSIC",
    kicker: "DISCOGRAPHY",
    ja: "楽曲",
    lead: ["歌声で届ける、もうひとつの物語。", "オリジナル楽曲と歌ってみたを、よく聴かれている順に。"],
    empty: "楽曲情報は準備中です。",
    listen: "YouTube で聴く",
    stream: "配信で聴く",
    endTitle: "MORE SONGS",
    endText: "そのほかの歌声は、YouTube チャンネルで。",
    endCta: "YouTube で聴く"
}

export const GOODS_COPY = {
    index: "07",
    title: "GOODS",
    kicker: "OFFICIAL GOODS",
    ja: "グッズ",
    lead: ["旅の記録を、手のひらサイズに。", "観測の思い出を持ち帰れるアイテムたち。"],
    empty: "グッズ情報は準備中です。",
    shop: "ショップで見る",
    soon: "近日販売"
}

export const WORKS_COPY = {
    index: "08",
    title: "WORKS",
    kicker: "ACTIVITY LOG",
    ja: "実績",
    lead: ["企業案件、公式ゲーム・IP、歌唱やボイス、メディア、イベント。", "マホニア観測局の活動記録から、代表的なものを抜粋しています。"],
    more: "VIEW MORE",
    moreJa: "すべての活動記録を見る",
    empty: "活動記録は準備中です。",
    open: "詳しく見る"
}

export const WORKS_PAGE_COPY = {
    title: "WORKS",
    kicker: "ARCHIVE",
    ja: "活動記録アーカイブ",
    lead: ["マホニア観測局に保管されている活動ログ。", "企業案件、公式ゲーム・IP、大規模コラボ、歌唱・ボイス、メディア、イベントの記録を閲覧できます。"],
    search: "企業名・案件名・作品名・担当内容で検索",
    searchLabel: "活動記録を検索",
    all: "すべて",
    empty: "該当する記録は見つかりませんでした。",
    clear: "条件をクリア",
    back: "TOP に戻る",
    open: "記録を開く"
}

export const CONTACT_COPY = {
    index: "09",
    title: "CONTACT",
    kicker: "MESSAGE",
    ja: "お問い合わせ",
    // Each inner array is one paragraph, each entry one line (see CopyLines).
    body: [
        ["企業様・個人様を問わず、お気軽にご相談ください。"]
    ],
    // The rest of the description, shown in slightly smaller type.
    note: [
        [
            "内容を確認のうえ順次対応いたしますが、",
            "すべてのお問い合わせに個別でお返事できない場合がございます。"
        ],
        ["あらかじめご了承ください。"]
    ],
    success: "メッセージを受け取りました。内容を確認のうえ、必要に応じてご返信いたします。"
}

export const CONTACT_CATEGORIES = [
    "お仕事のご依頼",
    "コラボレーションのご相談",
    "ファンレター・メッセージ",
    "その他"
] as const

export const CONTACT_LIMITS = {
    name: 80,
    email: 254,
    messageMin: 10,
    messageMax: 3000
}
